import React, { useMemo, useState, useEffect, useCallback } from "react";
import { Link, useNavigate } from "react-router-dom";
import { IoHomeOutline, IoArrowBack } from "react-icons/io5";
import { CgNotes } from "react-icons/cg";
import { GrDocumentSound } from "react-icons/gr";
import { IoIosLink } from "react-icons/io";
import { HiOutlineUserGroup } from "react-icons/hi";
import { FiX, FiDownload } from "react-icons/fi";
import axios from "axios";
import "./ProgramManagerReviewedApplication.css";
import ENDPOINT_URL from "../../config";

/* ----------------------
   Small helpers
   ---------------------- */
function formatDate(d) {
  try {
    return new Date(d).toLocaleString();
  } catch {
    return "-";
  }
}

function toCSV(rows = []) {
  if (!Array.isArray(rows) || rows.length === 0) return "";
  const keys = Object.keys(rows[0]);
  const header = keys.join(",");
  const lines = rows.map((r) =>
    keys
      .map((k) => {
        let v = r[k] === null || r[k] === undefined ? "" : String(r[k]);
        // escape double quotes
        v = v.replace(/"/g, '""');
        // wrap if contains comma or quote
        if (v.includes(",") || v.includes('"') || v.includes("\n")) v = `"${v}"`;
        return v;
      })
      .join(",")
  );
  return [header, ...lines].join("\n");
}

/* ----------------------
   UI pieces
   ---------------------- */
function ScorePill({ label, value }) {
  return (
    <div
      style={{
        display: "flex",
        flexDirection: "column",
        alignItems: "flex-start",
        gap: 6,
        padding: 8,
        borderRadius: 8,
        background: "#fafafa",
        border: "1px solid #eee",
        minWidth: 110,
      }}
    >
      <div style={{ fontSize: 12, color: "#666" }}>{label}</div>
      <div style={{ fontWeight: 700, fontSize: 16 }}>
        {typeof value === "number" ? value : "-"}
      </div>
    </div>
  );
}

/* ----------------------
   ReviewsDashboard with improved modal
   ---------------------- */
function ReviewsDashboard({ reviews = [] }) {
  const safeReviews = Array.isArray(reviews)
    ? reviews
    : reviews && Array.isArray(reviews.data)
    ? reviews.data
    : [];

  const [selectedApplicantEmail, setSelectedApplicantEmail] = useState(null);

  // Group reviews by applicant email
  const grouped = useMemo(() => {
    const map = new Map();
    for (const r of safeReviews) {
      const key = (r.emailOfApplicant || r.nameOfApplicant || "unknown")
        .toString()
        .trim();
      if (!map.has(key)) map.set(key, []);
      map.get(key).push(r);
    }

    return Array.from(map.entries()).map(([email, items]) => {
      const name = items[0]?.nameOfApplicant || "-";
      const count = items.length;
      const avgScore = (() => {
        const scoreKeys = [
          "innovativeness",
          "marketOpportunity",
          "teamAndExpertise",
          "iprInclusivity",
          "businessModelAndMarketStrategy",
          "hasSocialImpact",
        ];
        let total = 0;
        let n = 0;
        for (const it of items) {
          let s = 0;
          let c = 0;
          for (const k of scoreKeys) {
            if (typeof it[k] === "number") {
              s += it[k];
              c += 1;
            }
          }
          if (c > 0) {
            total += s / c;
            n += 1;
          }
        }
        return n > 0 ? +(total / n).toFixed(2) : "-";
      })();

      return { email, name, count, avgScore, items };
    });
  }, [safeReviews]);

  const openDetails = (email) => setSelectedApplicantEmail(email);
  const closeDetails = () => setSelectedApplicantEmail(null);

  const selectedGroup = grouped.find((g) => g.email === selectedApplicantEmail);

  // keyboard: close modal on Escape
  useEffect(() => {
    function onKey(e) {
      if (e.key === "Escape" && selectedApplicantEmail) closeDetails();
    }
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [selectedApplicantEmail]);

  const downloadCSVForSelected = useCallback(() => {
    if (!selectedGroup) return;
    const csv = toCSV(selectedGroup.items);
    const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `${selectedGroup.name.replace(/\s+/g, "_")}_reviews.csv`;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  }, [selectedGroup]);

  return (
    <div style={{ padding: 8 }}>
      {/* <h2 style={{ fontSize: 20, marginBottom: 8 }}>Reviewed Applications</h2> */}

      <div
        className="table-wrapper-programManagerreviewedapplication"
        style={{ border: "1px solid #eee", borderRadius: 8, overflow: "hidden" }}
      >
        <table
          className="reviewer-table-programManagerreviewedapplication"
          style={{ width: "100%", borderCollapse: "collapse" }}
        >
          <thead style={{ background: "#fafafa" }}>
            <tr>
              <th style={{ padding: 12, textAlign: "left" }}>Applicant</th>
              <th style={{ padding: 12, textAlign: "left" }}>Email</th>
              <th style={{ padding: 12, textAlign: "center", width: 120 }}>
                Reviews
              </th>
              <th style={{ padding: 12, textAlign: "center", width: 120 }}>
                Avg Score
              </th>
              <th style={{ padding: 12, textAlign: "center", width: 140 }}>
                Action
              </th>
            </tr>
          </thead>
          <tbody>
            {grouped.length === 0 && (
              <tr>
                <td
                  colSpan={5}
                  style={{ textAlign: "center", padding: 20, color: "#666" }}
                >
                  No reviews to show
                </td>
              </tr>
            )}

            {grouped.map((g) => (
              <tr key={g.email} style={{ borderTop: "1px solid #f0f0f0" }}>
                <td style={{ padding: 12 }}>{g.name}</td>
                <td style={{ padding: 12 }}>{g.email}</td>
                <td style={{ padding: 12, textAlign: "center" }}>{g.count}</td>
                <td style={{ padding: 12, textAlign: "center" }}>{g.avgScore}</td>
                <td style={{ padding: 12, textAlign: "center" }}>
                  <button
                    onClick={() => openDetails(g.email)}
                    className="view-button-programManagerreviewedapplication"
                    style={{
                      padding: "8px 12px",
                      borderRadius: 8,
                      border: "none",
                      background: "#2563eb",
                      color: "#fff",
                      cursor: "pointer",
                    }}
                  >
                    View
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Modal */}
      {selectedGroup && (
        <div
          role="dialog"
          aria-modal="true"
          className="modal-overlay-programManagerreviewedapplication"
          style={{
            position: "fixed",
            inset: 0,
            background: "rgba(0,0,0,0.5)",
            zIndex: 1200,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            padding: 20,
          }}
          onClick={(e) => {
            if (e.target === e.currentTarget) closeDetails();
          }}
        >
          <div
            className="modal-programManagerreviewedapplication"
            style={{
              width: "min(1100px, 98%)",
              maxHeight: "90vh",
              background: "#fff",
              borderRadius: 12,
              boxShadow: "0 10px 30px rgba(0,0,0,0.15)",
              overflow: "hidden",
              display: "flex",
              flexDirection: "column",
            }}
          >
            {/* Header */}
            <div
              style={{
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                padding: 16,
                borderBottom: "1px solid #eee",
              }}
            >
              <div style={{ display: "flex", gap: 12, alignItems: "center" }}>
                <div style={{ display: "flex", flexDirection: "column" }}>
                  <div style={{ fontSize: 18, fontWeight: 700 }}>
                    {selectedGroup.name}
                  </div>
                  <div style={{ color: "#666", fontSize: 13 }}>
                    {selectedGroup.email} · {selectedGroup.count} review(s)
                  </div>
                </div>
              </div>

              <div style={{ display: "flex", gap: 8, alignItems: "center" }}>
                <button
                  onClick={downloadCSVForSelected}
                  style={{
                    display: "inline-flex",
                    gap: 8,
                    alignItems: "center",
                    padding: "8px 12px",
                    borderRadius: 8,
                    border: "1px solid #e6e6e6",
                    background: "#694b9dff",
                    cursor: "pointer",
                  }}
                  title="Download CSV"
                >
                  <FiDownload />
                  Export CSV
                </button>

                <button
                  onClick={closeDetails}
                  aria-label="Close"
                  style={{
                    width: 40,
                    height: 40,
                    borderRadius: 10,
                    border: "none",
                    background: "#820c1aff",
                    display: "inline-flex",
                    alignItems: "center",
                    justifyContent: "center",
                    cursor: "pointer",
                  }}
                >
                  <FiX size={20} />
                </button>
              </div>
            </div>

            {/* Body - two column: left summary + right scrollable reviews */}
            <div
              style={{ display: "flex", gap: 16, flex: 1, overflow: "hidden" }}
            >
              {/* Left summary */}
              <aside
                style={{
                  width: 320,
                  padding: 16,
                  borderRight: "1px solid #f3f3f3",
                  background: "#fafafa",
                }}
              >
                <div style={{ marginBottom: 12 }}>
                  <div style={{ fontSize: 12, color: "#666" }}>
                    Average score (per-review)
                  </div>
                  <div
                    style={{ marginTop: 6, fontSize: 22, fontWeight: 800 }}
                  >
                    {selectedGroup.avgScore}
                  </div>
                </div>

                <div style={{ marginTop: 12 }}>
                  <div style={{ fontSize: 12, color: "#666" }}>
                    Review count
                  </div>
                  <div
                    style={{ marginTop: 6, fontSize: 18, fontWeight: 700 }}
                  >
                    {selectedGroup.count}
                  </div>
                </div>

                <div style={{ marginTop: 12 }}>
                  <div style={{ fontSize: 12, color: "#666" }}>
                    Last reviewed
                  </div>
                  <div style={{ marginTop: 6 }}>
                    {formatDate(selectedGroup.items[0]?.createdAt)}
                  </div>
                </div>

                <div style={{ marginTop: 18 }}>
                  <div style={{ fontSize: 13, fontWeight: 700 }}>
                    Quick Scores (first review)
                  </div>
                  <div
                    style={{
                      display: "grid",
                      gridTemplateColumns: "1fr 1fr",
                      gap: 8,
                      marginTop: 8,
                    }}
                  >
                    <ScorePill
                      label="Innovativeness"
                      value={selectedGroup.items[0]?.innovativeness}
                    />
                    <ScorePill
                      label="Market Opportunity"
                      value={selectedGroup.items[0]?.marketOpportunity}
                    />
                    <ScorePill
                      label="Team & Expertise"
                      value={selectedGroup.items[0]?.teamAndExpertise}
                    />
                    <ScorePill
                      label="IPR Inclusivity"
                      value={selectedGroup.items[0]?.iprInclusivity}
                    />
                    <ScorePill
                      label="Business Model"
                      value={
                        selectedGroup.items[0]
                          ?.businessModelAndMarketStrategy
                      }
                    />
                    <ScorePill
                      label="Social Impact"
                      value={selectedGroup.items[0]?.hasSocialImpact}
                    />
                  </div>
                </div>
              </aside>

              {/* Right: scrollable reviews list */}
              <div
                style={{ flex: 1, overflowY: "auto", padding: 16 }}
              >
                {selectedGroup.items.map((rev, idx) => (
                  <article
                    key={idx}
                    style={{
                      padding: 12,
                      borderRadius: 10,
                      border: "1px solid #eee",
                      marginBottom: 12,
                      background: "#fff",
                      boxShadow: "0 1px 4px rgba(12,12,12,0.02)",
                    }}
                  >
                    <header
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                      }}
                    >
                      <div>
                        <div style={{ fontWeight: 800 }}>
                          {rev.nameOfReviewer || rev.emailOfReviewer}
                        </div>
                        <div
                          style={{ fontSize: 12, color: "#666" }}
                        >
                          {rev.emailOfReviewer}
                        </div>
                      </div>
                      <div
                        style={{ fontSize: 12, color: "#666" }}
                      >
                        {formatDate(rev.createdAt)}
                      </div>
                    </header>

                    <div
                      style={{
                        marginTop: 12,
                        display: "grid",
                        gridTemplateColumns: "repeat(3, 1fr)",
                        gap: 10,
                      }}
                    >
                      <ScorePill
                        label="Innovativeness"
                        value={rev.innovativeness}
                      />
                      <ScorePill
                        label="Market Opportunity"
                        value={rev.marketOpportunity}
                      />
                      <ScorePill
                        label="Team & Expertise"
                        value={rev.teamAndExpertise}
                      />
                      <ScorePill
                        label="IPR Inclusivity"
                        value={rev.iprInclusivity}
                      />
                      <ScorePill
                        label="Business Model"
                        value={rev.businessModelAndMarketStrategy}
                      />
                      <ScorePill
                        label="Social Impact"
                        value={rev.hasSocialImpact}
                      />
                    </div>

                    {rev.remark && (
                      <div style={{ marginTop: 12 }}>
                        <div
                          style={{
                            fontSize: 12,
                            color: "#777",
                            marginBottom: 6,
                          }}
                        >
                          Remark
                        </div>
                        <div style={{ whiteSpace: "pre-wrap" }}>
                          {rev.remark}
                        </div>
                      </div>
                    )}
                  </article>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/* ----------------------
   Main page component
   ---------------------- */
export default function ProgramManagerReviewedApplication() {
  const navigate = useNavigate();
  const [user] = useState({
    username: "Program Manager",
    email: "pm@example.com",
  });
  const [reviews, setReviews] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // NEW: global search + date filters
  const [search, setSearch] = useState("");
  const [fromDate, setFromDate] = useState("");
  const [toDate, setToDate] = useState("");

  const API_PATH = `${ENDPOINT_URL}/api/review/get-form`;

  useEffect(() => {
    let cancelled = false;

    async function fetchReviews() {
      setLoading(true);
      setError(null);
      try {
        const res = await axios.get(API_PATH);
        let arr = [];
        if (Array.isArray(res.data)) arr = res.data;
        else if (res.data && Array.isArray(res.data.data)) arr = res.data.data;
        else {
          const maybeArray = Object.values(res.data || {}).find((v) =>
            Array.isArray(v)
          );
          arr = Array.isArray(maybeArray) ? maybeArray : [];
        }
        if (!cancelled) setReviews(arr);
      } catch (err) {
        if (!cancelled) {
          console.error("Error fetching review data", err);
          setError("Could not load reviews. Check console for details.");
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    }

    fetchReviews();
    return () => {
      cancelled = true;
    };
  }, [API_PATH]);

  const handleLogout = () => navigate("/login");

  // ---------- FILTER LOGIC (global search + date range) ----------
  const filteredReviews = useMemo(() => {
    let result = Array.isArray(reviews) ? [...reviews] : [];

    const q = search.trim().toLowerCase();

    if (q) {
      result = result.filter((r) => {
        const fields = [
          r.nameOfApplicant,
          r.emailOfApplicant,
          r.nameOfReviewer,
          r.emailOfReviewer,
          r.reviewFormId,
        ];
        return fields
          .filter(Boolean)
          .some((f) => String(f).toLowerCase().includes(q));
      });
    }

    if (fromDate) {
      const from = new Date(fromDate);
      from.setHours(0, 0, 0, 0);
      result = result.filter((r) => {
        if (!r.createdAt) return false;
        const d = new Date(r.createdAt);
        return d >= from;
      });
    }

    if (toDate) {
      const to = new Date(toDate);
      to.setHours(23, 59, 59, 999);
      result = result.filter((r) => {
        if (!r.createdAt) return false;
        const d = new Date(r.createdAt);
        return d <= to;
      });
    }

    return result;
  }, [reviews, search, fromDate, toDate]);

  const clearFilters = () => {
    setSearch("");
    setFromDate("");
    setToDate("");
  };

  return (
    <div className="dashboard-homepage-programManagerreviewedapplication">
      {/* Sidebar */}
      <aside className="sidebar-homepage-programManagerreviewedapplication">
        <div className="logo-container-homepage-programManagerreviewedapplication">
          <div className="logo-homepage-programManagerreviewedapplication">
            <img
              src="/navbar/drishtilogo.jpg"
              alt="Logo"
              className="dristilogo-homepage-programManagerreviewedapplication"
            />
          </div>
        </div>

        <div className="nav-container-homepage-programManagerreviewedapplication">
          <nav className="nav-homepage-programManagerreviewedapplication">
            <ul>
              <li>
                <Link to="/homepage">
                  <IoHomeOutline className="nav-icon-homepage-programManagerreviewedapplication" />
                  Homepage
                </Link>
              </li>

              <li>
                <Link to="/form">
                  <CgNotes className="nav-icon-homepage-programManagerreviewedapplication" />
                  Create Query Form
                </Link>
              </li>

              <li>
                <Link to="/cohorts">
                  <GrDocumentSound className="nav-icon-homepage-programManagerreviewedapplication" />
                  Cohorts
                </Link>
              </li>

              <li>
                <Link to="/pipeline">
                  <IoIosLink className="nav-icon-homepage-programManagerreviewedapplication" />
                  Pipeline
                </Link>
              </li>

              <li className="active-homepage-programManagerreviewedapplication">
                <Link to="/manage-reviewers">
                  <HiOutlineUserGroup className="nav-icon-homepage-programManagerreviewedapplication" />
                  Manage Reviewers
                </Link>
              </li>
            </ul>
          </nav>
        </div>
      </aside>

      {/* Header */}
      <header className="header-homepage-programManagerreviewedapplication">
        <span className="founder-homepage-programManagerreviewedapplication">
          Program Manager — Reviewed Applications
        </span>

        <div className="profile-section-homepage-programManagerreviewedapplication">
          <div className="user-info-homepage-programManagerreviewedapplication">
            <span className="user-initials-homepage-programManagerreviewedapplication">
              <img
                src="/navbar/login.png"
                alt="Login"
                style={{ width: "40px" }}
              />
            </span>

            <div className="user-details-homepage-programManagerreviewedapplication">
              <span className="user-name-homepage-programManagerreviewedapplication">
                {user.username}
              </span>
              <br />
              <span className="user-email-homepage-programManagerreviewedapplication">
                {user.email}
              </span>
            </div>
          </div>

          <button
            className="logout-button-homepage-programManagerreviewedapplication"
            onClick={handleLogout}
            style={{ marginLeft: "20px", padding: "8px" }}
          >
            Logout
          </button>
        </div>
      </header>

      {/* Main */}
      <main className="main-content-homepage-programManagerreviewedapplication">
        <section className="content-homepage-programManagerreviewedapplication">
          <div
            className="reviewer-header-programManagerreviewedapplication"
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
              marginBottom: "8px",
            }}
          >
            <h3 style={{ margin: 0 }}>Reviewed Applications</h3>

            <div style={{ display: "flex", gap: "10px" }}>
              <button
                className="action-btn-programManagerreviewedapplication"
                onClick={() => navigate("/homepage")}
                style={{ display: "flex", alignItems: "center", gap: "6px" }}
              >
                <IoArrowBack size={18} />
                Back to Homepage
              </button>
            </div>
          </div>

          {/* Filters: global search + date range */}
          <div
            style={{
              marginBottom: 12,
              display: "flex",
              flexWrap: "wrap",
              gap: 10,
              alignItems: "center",
            }}
          >
            <input
              type="text"
              placeholder="Search applicant / reviewer / email"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              style={{
                padding: "6px 10px",
                borderRadius: 8,
                border: "1px solid #ddd",
                minWidth: 260,
                flex: "1 1 240px",
              }}
            />

            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              <div>
                <label
                  style={{ fontSize: 12, color: "#555", }}
                >
                  From
                </label>
                <input
                  type="date"
                  value={fromDate}
                  onChange={(e) => setFromDate(e.target.value)}
                  style={{
                    padding: "6px 10px",
                    borderRadius: 8,
                    border: "1px solid #ddd",
                  }}
                />
              </div>

              <div>
                <label
                  style={{ fontSize: 12, color: "#555" }}
                >
                  To
                </label>
                <input
                  type="date"
                  value={toDate}
                  onChange={(e) => setToDate(e.target.value)}
                  style={{
                    padding: "6px 10px",
                    borderRadius: 8,
                    border: "1px solid #ddd",
                  }}
                />
              </div>

              <button
                type="button"
                onClick={clearFilters}
                style={{
                  alignSelf: "flex-end",
                  padding: "6px 10px",
                  borderRadius: 8,
                  border: "1px solid #e5e7eb",
                  background: "#0d5fb1ff",
                  cursor: "pointer",
                  fontSize: 13,
                }}
              >
                Clear
              </button>
            </div>
          </div>

          {/* Loading / Error handling */}
          {loading ? (
            <div style={{ padding: 20 }}>Loading reviews...</div>
          ) : error ? (
            <div style={{ padding: 20, color: "red" }}>{error}</div>
          ) : (
            <ReviewsDashboard reviews={filteredReviews} />
          )}
        </section>
      </main>
    </div>
  );
}
